import { useState } from 'react'
import { useQuery } from '@connectrpc/connect-query'
import { Modal } from '@magnetic/modal'
import { Button } from '@magnetic/button'
import { Input } from '@magnetic/input'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Spinner } from '@magnetic/spinner'
import { Text } from '@magnetic/text'
import { useAuth } from '@/lib/use-auth'
import { listUsers, startImpersonation } from '@/lib/api'
import { invalidateAllConnectQueries } from '@/lib/connect-query-invalidation'
import { User } from '@/lib/icons'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'

interface ImpersonateUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ImpersonateUserDialog({ open, onOpenChange }: ImpersonateUserDialogProps) {
  const [search, setSearch] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const { refreshAuth } = useAuth()

  const { data, isLoading } = useQuery(listUsers, {}, { enabled: open })
  const users = data?.users ?? []

  const impersonateMutation = useMutationWithToast(startImpersonation, {
    successTitle: 'Impersonation started',
    successMessage: () => {
      const user = users.find(u => u.id === selectedUserId)
      return `Now viewing as ${user?.email ?? 'user'}`
    },
    errorTitle: 'Failed to start impersonation',
    invalidate: invalidateAllConnectQueries,
    onSuccess: async () => {
      await refreshAuth()
      onOpenChange(false)
      setSelectedUserId(null)
      setSearch('')
      setShowDropdown(false)
    },
  })

  // Filter out SuperAdmins - they cannot be impersonated
  const filteredUsers = users.filter(
    (u) => !u.isSuperAdmin && u.email.toLowerCase().includes(search.toLowerCase())
  )

  const selectedUser = users.find(u => u.id === selectedUserId)

  const handleSelectUser = (userId: string, email: string) => {
    setSelectedUserId(userId)
    setSearch(email)
    setShowDropdown(false)
  }

  const handleStartImpersonation = () => {
    const user = users.find(u => u.id === selectedUserId)
    if (!user) return

    impersonateMutation.mutate({
      userId: user.id,
      email: user.email,
      name: user.name,
      groups: [...user.groups],
    })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    setSelectedUserId(null)
    setShowDropdown(true)
  }

  const handleInputFocus = () => {
    setShowDropdown(true)
  }

  return (
    <Modal isOpen={open} onClose={() => onOpenChange(false)} size="md">
      <Modal.Content>
        <Flex direction="vertical" gap="lg" style={{ padding: 24 }}>
          <Flex direction="vertical" gap="sm">
            <Heading size="section">Impersonate User</Heading>
            <Text size="sm" color="light">
              Search for a regular user by email to impersonate them. SuperAdmins cannot be impersonated.
            </Text>
          </Flex>

          <Flex direction="vertical" gap="md">
            <Flex direction="vertical" gap="sm" style={{ position: 'relative' }}>
              <Text size="sm" weight="semi-bold">User Email</Text>
              <Input
                label="User Email"
                hiddenLabel
                placeholder="Search by email address..."
                value={search}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                disabled={isLoading}
              />

              {showDropdown && search && filteredUsers.length > 0 && (
                <Flex direction="vertical" style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: 4,
                  maxHeight: 240,
                  overflowY: 'auto',
                  border: '1px solid var(--mds-color-theme-outline-secondary-normal)',
                  borderRadius: 8,
                  backgroundColor: 'var(--mds-color-theme-background-primary-normal)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000,
                }}>
                  {filteredUsers.slice(0, 10).map((user) => (
                    <Button
                      key={user.id}
                      onClick={() => handleSelectUser(user.id, user.email)}
                      kind="tertiary"
                      style={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        borderBottom: '1px solid var(--mds-color-theme-outline-secondary-normal)',
                        borderRadius: 0,
                      }}
                    >
                      <Text size="sm">{user.email}</Text>
                    </Button>
                  ))}
                  {filteredUsers.length > 10 && (
                    <Flex style={{ padding: '8px 16px', opacity: 0.6 }}>
                      <Text size="xs">
                        {filteredUsers.length - 10} more results... Keep typing to narrow down
                      </Text>
                    </Flex>
                  )}
                </Flex>
              )}
            </Flex>

            {selectedUser && (
              <Flex direction="vertical" style={{
                padding: 16,
                border: '1px solid var(--mds-color-theme-outline-secondary-normal)',
                borderRadius: 8,
                backgroundColor: 'var(--base-bg-default)',
              }}>
                <Flex direction="vertical" gap="xs">
                  <Text size="xs" weight="semi-bold" color="light" style={{ textTransform: 'uppercase' }}>
                    Selected User
                  </Text>
                  <Flex align="center" gap="sm">
                    <User size={20} style={{ opacity: 0.7 }} />
                    <Flex direction="vertical">
                      <Text size="sm" weight="semi-bold">{selectedUser.email}</Text>
                      <Text size="xs" color="light">{selectedUser.name}</Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            )}

            {isLoading && (
              <Flex align="center" justify="center" gap="sm" style={{ padding: 32 }}>
                <Spinner size="md" />
                <Text size="sm" color="light">Loading users...</Text>
              </Flex>
            )}
          </Flex>

          <Flex justify="flex-end" gap="sm">
            <Button
              kind="secondary"
              onClick={() => {
                onOpenChange(false)
                setSearch('')
                setSelectedUserId(null)
                setShowDropdown(false)
              }}
              disabled={impersonateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStartImpersonation}
              disabled={!selectedUserId || impersonateMutation.isPending}
              icon={impersonateMutation.isPending
                ? <Spinner size="sm" />
                : <User size={16} />
              }
            >
              {impersonateMutation.isPending ? 'Starting...' : 'Start Impersonation'}
            </Button>
          </Flex>
        </Flex>
      </Modal.Content>
    </Modal>
  )
}
